import { MigrationInterface, QueryRunner } from 'typeorm'
import { Domain, getRepository } from '@things-factory/shell'
import { KpiValue, KpiValueInputType } from '../service/kpi-value/kpi-value'
import { Kpi, KpiPeriodType } from '../service/kpi/kpi'
import { KpiOrgScope } from '../service/kpi-org-scope/kpi-org-scope'
import * as fs from 'fs'
import * as path from 'path'

interface KpiValueSeedData {
  kpi_name: string
  org: string
  value_date: string
  value: number
  score?: number
  period_type: string
  input_type?: string
  source?: string
}

export class SeedKpiValues1752192090129 implements MigrationInterface {
  name = 'SeedKpiValues1752192090129'

  public async up(queryRunner: QueryRunner): Promise<void> {
    try {
      const repository = getRepository(KpiValue)
      const domainRepository = getRepository(Domain)
      const kpiRepository = getRepository(Kpi)
      const kpiOrgScopeRepository = getRepository(KpiOrgScope)

      // SYSTEM 도메인 확인
      const domain: Domain = await domainRepository.findOneBy({ name: 'SYSTEM' })
      if (!domain) {
        console.log('❌ SYSTEM domain not found')
        return
      }

      // JSON 파일 읽기
      const seedFilePath = path.join(__dirname, 'seed-data', 'kpi-values-seed.json')
      const seedData: KpiValueSeedData[] = JSON.parse(fs.readFileSync(seedFilePath, 'utf8'))

      console.log(`📊 Processing ${seedData.length} KPI values from JSON seed`)

      // 모든 KPI 미리 로드 (성능 최적화)
      const kpis = await kpiRepository.find({
        where: { domain: { id: domain.id } }
      })
      const kpiMap = new Map<string, Kpi>()
      kpis.forEach(kpi => kpiMap.set(kpi.name, kpi))

      // 모든 KpiOrgScope 미리 로드 (성능 최적화)
      const kpiOrgScopes = await kpiOrgScopeRepository.find({
        where: { domain: { id: domain.id } }
      })
      const kpiOrgScopeMap = new Map<string, KpiOrgScope>()
      kpiOrgScopes.forEach(scope => {
        // org 필드(legacy)와 entityName 둘 다 키로 사용
        if (scope.org) kpiOrgScopeMap.set(scope.org, scope)
        if (scope.entityName) kpiOrgScopeMap.set(scope.entityName, scope)
      })

      for (const valueData of seedData) {
        try {
          const kpi = kpiMap.get(valueData.kpi_name)
          if (!kpi) {
            console.log(`⚠️  KPI not found: ${valueData.kpi_name}`)
            continue
          }

          const kpiOrgScope = kpiOrgScopeMap.get(valueData.org)
          if (!kpiOrgScope) {
            console.log(`⚠️  KpiOrgScope not found: ${valueData.org}`)
            continue
          }

          // 기존 값 확인
          const existingValue = await repository.findOne({
            where: {
              kpi: { id: kpi.id },
              kpiOrgScope: { id: kpiOrgScope.id },
              valueDate: valueData.value_date,
              domain: { id: domain.id }
            }
          })

          if (existingValue) {
            console.log(`⚠️  KPI Value already exists: ${valueData.kpi_name} - ${valueData.org}`)
            continue
          }

          // KpiValueInputType 변환
          let inputType: KpiValueInputType | undefined
          if (valueData.input_type === 'MANUAL') {
            inputType = KpiValueInputType.MANUAL
          } else if (valueData.input_type === 'AUTO') {
            inputType = KpiValueInputType.AUTO
          }

          // KpiPeriodType 변환
          let periodType: KpiPeriodType = KpiPeriodType.DAY
          if (valueData.period_type === 'MONTH') {
            periodType = KpiPeriodType.MONTH
          } else if (valueData.period_type === 'QUARTER') {
            periodType = KpiPeriodType.QUARTER
          } else if (valueData.period_type === 'YEAR') {
            periodType = KpiPeriodType.YEAR
          }

          const kpiValue = await repository.save({
            kpi,
            kpiOrgScope,
            valueDate: valueData.value_date,
            value: valueData.value,
            score: valueData.score,
            periodType,
            inputType,
            source: valueData.source,
            version: kpi.version || 1,
            domain
          })

          console.log(`✅ Created KPI Value: ${valueData.kpi_name} - ${valueData.org} = ${valueData.value}`)
        } catch (error) {
          console.error(`❌ Error creating KPI Value "${valueData.kpi_name} - ${valueData.org}":`, error)
        }
      }

      console.log(`🎉 Successfully processed KPI values from JSON seed`)
    } catch (error) {
      console.error('❌ Error seeding KPI values from JSON:', error)
      throw error
    }
  }

  public async down(queryRunner: QueryRunner): Promise<void> {
    try {
      const repository = getRepository(KpiValue)
      const domainRepository = getRepository(Domain)
      const kpiRepository = getRepository(Kpi)
      const kpiOrgScopeRepository = getRepository(KpiOrgScope)

      const domain: Domain = await domainRepository.findOneBy({ name: 'SYSTEM' })
      if (!domain) {
        console.log('⚠️  SYSTEM domain not found. Nothing to delete.')
        return
      }

      // JSON 파일 읽기
      const seedFilePath = path.join(__dirname, 'seed-data', 'kpi-values-seed.json')
      const seedData: KpiValueSeedData[] = JSON.parse(fs.readFileSync(seedFilePath, 'utf8'))

      console.log(`🗑️  Cleaning up ${seedData.length} KPI values`)

      // 모든 KPI 미리 로드
      const kpis = await kpiRepository.find({
        where: { domain: { id: domain.id } }
      })
      const kpiMap = new Map<string, Kpi>()
      kpis.forEach(kpi => kpiMap.set(kpi.name, kpi))

      // 모든 KpiOrgScope 미리 로드
      const kpiOrgScopes = await kpiOrgScopeRepository.find({
        where: { domain: { id: domain.id } }
      })
      const kpiOrgScopeMap = new Map<string, KpiOrgScope>()
      kpiOrgScopes.forEach(scope => {
        // org 필드(legacy)와 entityName 둘 다 키로 사용
        if (scope.org) kpiOrgScopeMap.set(scope.org, scope)
        if (scope.entityName) kpiOrgScopeMap.set(scope.entityName, scope)
      })

      for (const valueData of seedData.reverse()) {
        try {
          const kpi = kpiMap.get(valueData.kpi_name)
          if (!kpi) {
            continue
          }

          const kpiOrgScope = kpiOrgScopeMap.get(valueData.org)
          if (!kpiOrgScope) {
            continue
          }

          const kpiValue = await repository.findOne({
            where: {
              kpi: { id: kpi.id },
              kpiOrgScope: { id: kpiOrgScope.id },
              valueDate: valueData.value_date,
              domain: { id: domain.id }
            }
          })

          if (kpiValue) {
            await repository.delete(kpiValue.id)
            console.log(`✅ Deleted KPI Value: ${valueData.kpi_name} - ${valueData.org}`)
          }
        } catch (error) {
          console.error(`❌ Error deleting KPI Value "${valueData.kpi_name} - ${valueData.org}":`, error)
        }
      }

      console.log(`🎉 Successfully cleaned up KPI values`)
    } catch (error) {
      console.error('❌ Error cleaning up KPI values:', error)
      throw error
    }
  }
}
